/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2026.
 */
/* eslint-disable complexity */
import { useContext, useState } from 'react';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ContentContext } from '@/data/context/content';
import { useCouponStatus } from '@/data/Content/CouponStatus';
import { parseHTML } from '@/utils/parseHTML';
import { parseCouponDescription } from '@/utils/parseCouponDescription';

interface OrderCouponRestrictionProps {
	code: string;
	promotionId?: string;
	shortDescription?: string;
	minimumPurchase?: number;
	currentSubtotal?: number;
	pending?: boolean;
}

export const OrderCouponRestriction: React.FC<OrderCouponRestrictionProps> = ({
	code,
	promotionId,
	shortDescription: initialShortDescription,
	minimumPurchase,
	currentSubtotal,
	pending = false,
}) => {
	const { onPromoCodeRemove, inactivePromotionCodes } = useContext(ContentContext) as {
		onPromoCodeRemove?: (couponCode?: string) => Promise<void>;
		inactivePromotionCodes?: string[];
	};
	const inactive = Boolean(inactivePromotionCodes?.includes(code));
	const accentColor = inactive ? '#9E9E9E' : '#0467D2';
	const [open, setOpen] = useState(false);
	const {
		shortDescription: couponStatusShortDescription,
		longDescription: couponStatusLongDescription,
	} = useCouponStatus(promotionId);

	const sections = parseCouponDescription(couponStatusLongDescription);

	const shortDescription = inactive
		? 'Este cupón no aplica a tu carrito'
		: sections.descripcion ||
		  couponStatusShortDescription ||
		  initialShortDescription ||
		  'Cupón aplicado';
	const description = sections.restricciones;
	const hasRestrictions = Boolean(description);

	const pendingSuffix =
		pending && !inactive && minimumPurchase && (currentSubtotal ?? 0) < minimumPurchase
			? ` · Te faltan $${(minimumPurchase - (currentSubtotal ?? 0)).toLocaleString('es-MX')}`
			: '';

	if (!code) return null;

	const couponTitle = sections.descripcionCorta ? `${sections.descripcionCorta}: ${code}` : code;

	const handleRemove = () => {
		void onPromoCodeRemove?.(code);
	};

	return (
		<Box
			sx={{
				width: '100%',
				border: inactive ? '1px solid #C9C9C9' : '1px solid #1683F7',
				borderRadius: '3px',
				padding: '5px 7px',
				backgroundColor: inactive ? '#F5F5F5' : 'transparent',
				color: inactive ? '#757575' : 'inherit',
			}}
			data-testid={`coupon-card-${code}`}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
					<ConfirmationNumberOutlinedIcon sx={{ color: accentColor, fontSize: '14px' }} />
					<Typography
						sx={{
							fontSize: 'var(--font-size-small)',
							fontWeight: 700,
							lineHeight: 1.2,
							color: '#080808',
						}}
					>
						{parseHTML(couponTitle)}
					</Typography>
				</Stack>
				<IconButton
					aria-label={`Eliminar cupon ${code}`}
					size="small"
					onClick={handleRemove}
					sx={{ padding: 0, color: accentColor, visibility: pending ? 'hidden' : 'visible' }}
				>
					<CloseIcon sx={{ fontSize: '16px' }} />
				</IconButton>
			</Stack>
			<Typography
				sx={{
					color: '#080808',
					fontSize: 'var(--font-size-small)',
					lineHeight: 1.25,
				}}
			>
				{parseHTML(shortDescription)}
				{pendingSuffix}
				{hasRestrictions ? (
					<>
						{' '}
						<Typography
							component="button"
							onClick={() => setOpen((previous) => !previous)}
							sx={{
								border: 0,
								padding: 0,
								margin: 0,
								background: 'transparent',
								cursor: 'pointer',
								color: inactive ? '#9E9E9E' : '#0467D2',
								fontSize: 'var(--font-size-small)',
								whiteSpace: 'nowrap',
								display: 'inline',
								verticalAlign: 'baseline',
							}}
							data-testid={`coupon-restrictions-toggle-${code}`}
						>
							{open ? 'Ocultar' : 'Ver restricciones'}
						</Typography>
						<KeyboardArrowDownIcon
							sx={{
								fontSize: '1rem',
								color: inactive ? '#9E9E9E' : '#0467D2',
								transform: open ? 'rotate(180deg)' : 'none',
								transition: 'transform 0.2s',
								verticalAlign: 'middle',
							}}
						/>
					</>
				) : null}
			</Typography>
			{hasRestrictions ? (
				<Collapse in={open} timeout="auto" unmountOnExit>
					<Typography
						sx={{
							fontSize: 'var(--font-size-small)',
							color: inactive ? '#757575' : '#666',
							marginTop: '3px',
							lineHeight: 1.25,
							'& a': { color: 'inherit', textDecoration: 'underline' },
						}}
					>
						{parseHTML(description ?? '')}
					</Typography>
				</Collapse>
			) : null}
		</Box>
	);
};
